import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  listSubmissions,
  updateSubmission,
  deleteSubmission,
  getAdminStats,
  updatePublicStats,
  listDonations,
  updateDonation,
  deleteDonation,
  getGallery,
  uploadGalleryItem,
  deleteGalleryItem,
  listNotifications,
  markNotificationsRead,
  exportSubmissionsCSV,
  exportSubmissionsPDF,
  exportDonationsCSV,
  exportDonationsPDF,
  downloadDonationReceipt,
  triggerBrowserDownload,
  formatApiError,
} from '../lib/api';
import {
  LogOut,
  Mail,
  Phone,
  Building2,
  User,
  Calendar,
  Trash2,
  Filter,
  TrendingUp,
  Users,
  MapPin,
  Award,
  Inbox,
  CheckCircle2,
  Clock,
  Edit,
  Save,
  X,
  Home,
  Heart,
  DollarSign,
  ImageIcon,
  Upload,
  Bell,
  FileText,
  Hash,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const SUB_STATUS = {
  new: { label: 'Nueva', color: 'bg-sky-100 text-sky-700 border-sky-300', icon: <Inbox className="w-3 h-3" /> },
  contacted: { label: 'Contactada', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: <Clock className="w-3 h-3" /> },
  in_progress: { label: 'En Proceso', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: <TrendingUp className="w-3 h-3" /> },
  closed: { label: 'Cerrada', color: 'bg-green-100 text-green-700 border-green-300', icon: <CheckCircle2 className="w-3 h-3" /> },
};

const DONATION_STATUS = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  confirmed: { label: 'Confirmada', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  verified: { label: 'Verificada', color: 'bg-green-100 text-green-700 border-green-300' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700 border-red-300' },
};

const formatCurrency = (amount) =>
  `$${(amount || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [submissions, setSubmissions] = useState([]);
  const [donations, setDonations] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [adminStats, setAdminStats] = useState(null);

  const [subFilter, setSubFilter] = useState('all');
  const [donFilter, setDonFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedDonation, setSelectedDonation] = useState(null);

  const [editingStats, setEditingStats] = useState(false);
  const [statsForm, setStatsForm] = useState({
    athletes_helped: 0,
    provinces_reached: 0,
    success_rate: 0,
    years_active: 0,
  });

  // Gallery upload
  const fileInputRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', type: 'photo' });
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await getAdminStats();
      setAdminStats(res.data);
      if (!editingStats) setStatsForm(res.data.public_stats);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al cargar stats');
    }
  }, [editingStats]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await listSubmissions(subFilter);
      setSubmissions(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al cargar consultas');
    }
  }, [subFilter]);

  const fetchDonations = useCallback(async () => {
    try {
      const res = await listDonations(donFilter);
      setDonations(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al cargar donaciones');
    }
  }, [donFilter]);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await getGallery();
      setGalleryItems(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al cargar galería');
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await listNotifications(false);
      setNotifications(res.data);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);
  useEffect(() => { fetchDonations(); }, [fetchDonations]);
  useEffect(() => { fetchGallery(); }, [fetchGallery]);
  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Submission handlers
  const handleSubStatusChange = async (id, newStatus) => {
    try {
      await updateSubmission(id, { status: newStatus });
      toast.success('Estado actualizado');
      fetchSubmissions();
      fetchStats();
      if (selectedSubmission?.id === id) setSelectedSubmission({ ...selectedSubmission, status: newStatus });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  const handleDeleteSub = async (id) => {
    try {
      await deleteSubmission(id);
      toast.success('Consulta eliminada');
      setSelectedSubmission(null);
      fetchSubmissions();
      fetchStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  // Donation handlers
  const handleDonStatusChange = async (id, newStatus) => {
    try {
      await updateDonation(id, { status: newStatus });
      toast.success('Estado actualizado');
      fetchDonations();
      fetchStats();
      if (selectedDonation?.id === id) setSelectedDonation({ ...selectedDonation, status: newStatus });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  const handleDeleteDon = async (id) => {
    try {
      await deleteDonation(id);
      toast.success('Donación eliminada');
      setSelectedDonation(null);
      fetchDonations();
      fetchStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  // Stats edit
  const handleStatsUpdate = async (e) => {
    e.preventDefault();
    try {
      await updatePublicStats({
        athletes_helped: parseInt(statsForm.athletes_helped, 10) || 0,
        provinces_reached: parseInt(statsForm.provinces_reached, 10) || 0,
        success_rate: parseInt(statsForm.success_rate, 10) || 0,
        years_active: parseInt(statsForm.years_active, 10) || 0,
      });
      toast.success('Estadísticas actualizadas');
      setEditingStats(false);
      fetchStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  // Gallery upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo supera los 5MB');
      e.target.value = '';
      return;
    }
    const isVideo = file.type.startsWith('video/');
    const reader = new FileReader();
    reader.onload = () => {
      setUploadPreview(reader.result);
      setUploadForm((f) => ({ ...f, type: isVideo ? 'video' : 'photo' }));
    };
    reader.readAsDataURL(file);
  };

  const handleUploadGallery = async (e) => {
    e.preventDefault();
    if (!uploadPreview || !uploadForm.title.trim()) {
      toast.error('Subí un archivo y completá el título');
      return;
    }
    setUploading(true);
    try {
      await uploadGalleryItem({
        type: uploadForm.type,
        title: uploadForm.title,
        description: uploadForm.description,
        data_url: uploadPreview,
      });
      toast.success('Subido a galería');
      setUploadForm({ title: '', description: '', type: 'photo' });
      setUploadPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchGallery();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteGallery = async (id) => {
    try {
      await deleteGalleryItem(id);
      toast.success('Eliminado');
      fetchGallery();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Error');
    }
  };

  // Notifications
  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead([]);
      fetchNotifications();
      fetchStats();
    } catch {
      // silent
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/admin/login');
  };

  // Exports
  const handleExport = async (type, format) => {
    try {
      let blob, filename;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, '');
      if (type === 'submissions') {
        const res = format === 'csv'
          ? await exportSubmissionsCSV(subFilter)
          : await exportSubmissionsPDF(subFilter);
        blob = res.data;
        filename = `consultas_${stamp}.${format}`;
      } else {
        const res = format === 'csv'
          ? await exportDonationsCSV(donFilter)
          : await exportDonationsPDF(donFilter);
        blob = res.data;
        filename = `donaciones_${stamp}.${format}`;
      }
      triggerBrowserDownload(blob, filename);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const handleDownloadReceipt = async (donationId, donorName) => {
    try {
      const res = await downloadDonationReceipt(donationId);
      const safe = (donorName || 'donante').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
      triggerBrowserDownload(res.data, `certificado_${safe}_${donationId.slice(-6)}.pdf`);
      toast.success('Certificado descargado');
    } catch (err) {
      toast.error('Error al generar certificado');
    }
  };

  const handleShareByEmail = (donation) => {
    const subject = encodeURIComponent(
      `Certificado de Donación - Fundación Futuros del Deporte`
    );
    const amount = formatCurrency(donation.amount);
    const body = encodeURIComponent(
      `Hola ${donation.donor_name},\n\n` +
      `En nombre de la Fundación Futuros del Deporte queremos agradecerte tu generosa donación de ${amount}.\n\n` +
      `Adjuntamos el certificado de donación (PDF descargado del dashboard).\n\n` +
      `Tu aporte contribuye al desarrollo de jóvenes deportistas del interior argentino que sueñan con convertirse en atletas profesionales y de elite.\n\n` +
      `¡Muchas gracias por sumarte a este sueño!\n\n` +
      `---\n` +
      `Fundación Futuros del Deporte\n` +
      `CUIT: 30-71755372-8\n` +
      `sebastianrodriguezpsa@gmail.com\n` +
      `+54-380-4379609`
    );
    window.open(`mailto:${donation.donor_email}?subject=${subject}&body=${body}`, '_blank');
    toast.info('Recordá adjuntar el certificado descargado al email');
  };

  const handleShareByWhatsApp = (donation) => {
    const phone = (donation.donor_phone || '').replace(/[^0-9]/g, '');
    if (!phone) {
      toast.error('No hay teléfono del donante');
      return;
    }
    const amount = formatCurrency(donation.amount);
    const text = encodeURIComponent(
      `Hola ${donation.donor_name}! 🙌\n\n` +
      `En nombre de la *Fundación Futuros del Deporte* queremos agradecerte tu donación de *${amount}*.\n\n` +
      `Te enviamos por separado el certificado de donación en PDF.\n\n` +
      `¡Gracias por ayudar a más jóvenes deportistas a cumplir sus sueños! ⚽🏆`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const unreadCount = adminStats?.unread_notifications || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white font-bold">
                FD
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">Panel Administrativo</h1>
                <p className="text-xs text-sky-600 font-medium">Futuros del Deporte</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="notifications-bell"
                    variant="outline"
                    size="sm"
                    className="relative border-gray-300"
                  >
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span
                        data-testid="notifications-badge"
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-bold text-gray-900">Notificaciones</h3>
                    {unreadCount > 0 && (
                      <button
                        data-testid="mark-all-read-btn"
                        onClick={handleMarkAllRead}
                        className="text-xs text-sky-600 hover:text-sky-700 font-medium"
                      >
                        Marcar todas como leídas
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No hay notificaciones</p>
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`p-3 border-b border-gray-100 last:border-0 ${
                            !n.read ? 'bg-sky-50' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-2">
                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? 'bg-sky-500' : 'bg-gray-300'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-gray-900">{n.title}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatDate(n.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <span className="hidden sm:block text-sm text-gray-600">
                Hola, <span className="font-semibold">{user?.name}</span>
              </span>
              <Button
                data-testid="goto-site-btn"
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-gray-300"
              >
                <Home className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Ver Sitio</span>
              </Button>
              <Button
                data-testid="logout-btn"
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Salir</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {adminStats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Card data-testid="stat-card-submissions" className="p-5 border-2 border-sky-100">
              <div className="flex items-center justify-between mb-2">
                <Inbox className="w-7 h-7 text-sky-600" />
                <span className="text-2xl font-bold text-gray-900">{adminStats.submissions_stats.total}</span>
              </div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Consultas</p>
            </Card>
            <Card data-testid="stat-card-new-subs" className="p-5 border-2 border-sky-100">
              <div className="flex items-center justify-between mb-2">
                <Mail className="w-7 h-7 text-sky-500" />
                <span className="text-2xl font-bold text-gray-900">{adminStats.submissions_stats.new}</span>
              </div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Nuevas</p>
            </Card>
            <Card data-testid="stat-card-donations" className="p-5 border-2 border-pink-100">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-7 h-7 text-pink-500" />
                <span className="text-2xl font-bold text-gray-900">{adminStats.donations_stats.total}</span>
              </div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Donaciones</p>
            </Card>
            <Card data-testid="stat-card-pending-donations" className="p-5 border-2 border-amber-100">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-7 h-7 text-amber-500" />
                <span className="text-2xl font-bold text-gray-900">{adminStats.donations_stats.pending}</span>
              </div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Pendientes</p>
            </Card>
            <Card data-testid="stat-card-raised" className="p-5 border-2 border-green-100">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-7 h-7 text-green-600" />
                <span className="text-xl font-bold text-gray-900">
                  {formatCurrency(adminStats.donations_stats.verified_amount)}
                </span>
              </div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Recaudado</p>
            </Card>
          </div>
        )}

        <Tabs defaultValue="submissions" className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-4 mb-6">
            <TabsTrigger data-testid="tab-submissions" value="submissions">Consultas</TabsTrigger>
            <TabsTrigger data-testid="tab-donations" value="donations">Donaciones</TabsTrigger>
            <TabsTrigger data-testid="tab-gallery" value="gallery">Galería</TabsTrigger>
            <TabsTrigger data-testid="tab-stats" value="stats">Estadísticas</TabsTrigger>
          </TabsList>

          {/* === SUBMISSIONS === */}
          <TabsContent value="submissions">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Filter className="w-4 h-4 mr-2" /> Consultas
                    </h3>
                    <Select value={subFilter} onValueChange={setSubFilter}>
                      <SelectTrigger data-testid="filter-sub-status" className="w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="new">Nuevas</SelectItem>
                        <SelectItem value="contacted">Contactadas</SelectItem>
                        <SelectItem value="in_progress">En Proceso</SelectItem>
                        <SelectItem value="closed">Cerradas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex space-x-2 mb-4">
                    <Button
                      data-testid="export-sub-csv"
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('submissions', 'csv')}
                      className="flex-1 h-8 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                      CSV
                    </Button>
                    <Button
                      data-testid="export-sub-pdf"
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('submissions', 'pdf')}
                      className="flex-1 h-8 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      PDF
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {submissions.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No hay consultas</p>
                      </div>
                    ) : (
                      submissions.map((sub) => (
                        <button
                          key={sub.id}
                          data-testid={`submission-card-${sub.id}`}
                          onClick={() => setSelectedSubmission(sub)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            selectedSubmission?.id === sub.id
                              ? 'border-sky-400 bg-sky-50'
                              : 'border-gray-200 hover:border-sky-300'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 text-sm truncate">{sub.company_name}</div>
                          <p className="text-xs text-gray-600 truncate mb-2">{sub.contact_name}</p>
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center space-x-1 text-xs px-2 py-0.5 rounded-full border ${SUB_STATUS[sub.status]?.color}`}>
                              {SUB_STATUS[sub.status]?.icon}
                              <span>{SUB_STATUS[sub.status]?.label}</span>
                            </span>
                            <span className="text-xs text-gray-400">{formatDate(sub.created_at).split(',')[0]}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </Card>
              </div>
              <div className="lg:col-span-2">
                {selectedSubmission ? (
                  <Card data-testid="submission-detail" className="p-6">
                    <div className="flex items-start justify-between mb-6 pb-6 border-b border-gray-200">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedSubmission.company_name}</h2>
                        <p className="text-gray-600">Consulta de colaboración</p>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid="delete-submission-btn" variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar consulta?</AlertDialogTitle>
                            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid="confirm-delete-sub-btn" onClick={() => handleDeleteSub(selectedSubmission.id)} className="bg-red-600 hover:bg-red-700">
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <InfoRow icon={<Building2 className="w-5 h-5" />} label="Empresa" value={selectedSubmission.company_name} />
                      <InfoRow icon={<User className="w-5 h-5" />} label="Contacto" value={selectedSubmission.contact_name} />
                      <InfoRow icon={<Mail className="w-5 h-5" />} label="Email" value={selectedSubmission.email} href={`mailto:${selectedSubmission.email}`} />
                      <InfoRow icon={<Phone className="w-5 h-5" />} label="Teléfono" value={selectedSubmission.phone} href={`tel:${selectedSubmission.phone}`} />
                      <InfoRow icon={<Calendar className="w-5 h-5" />} label="Recibida" value={formatDate(selectedSubmission.created_at)} colSpan />
                    </div>
                    <div className="mb-6">
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Mensaje</p>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-gray-800 whitespace-pre-wrap">{selectedSubmission.message}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Estado</p>
                      <Select value={selectedSubmission.status} onValueChange={(v) => handleSubStatusChange(selectedSubmission.id, v)}>
                        <SelectTrigger data-testid="sub-status-select" className="w-full sm:w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Nueva</SelectItem>
                          <SelectItem value="contacted">Contactada</SelectItem>
                          <SelectItem value="in_progress">En Proceso</SelectItem>
                          <SelectItem value="closed">Cerrada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </Card>
                ) : (
                  <EmptyDetail message="Selecciona una consulta" />
                )}
              </div>
            </div>
          </TabsContent>

          {/* === DONATIONS === */}
          <TabsContent value="donations">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Filter className="w-4 h-4 mr-2" /> Donaciones
                    </h3>
                    <Select value={donFilter} onValueChange={setDonFilter}>
                      <SelectTrigger data-testid="filter-don-status" className="w-36 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="confirmed">Confirmadas</SelectItem>
                        <SelectItem value="verified">Verificadas</SelectItem>
                        <SelectItem value="rejected">Rechazadas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex space-x-2 mb-4">
                    <Button
                      data-testid="export-don-csv"
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('donations', 'csv')}
                      className="flex-1 h-8 text-xs border-pink-300 text-pink-700 hover:bg-pink-50"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                      CSV
                    </Button>
                    <Button
                      data-testid="export-don-pdf"
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('donations', 'pdf')}
                      className="flex-1 h-8 text-xs border-pink-300 text-pink-700 hover:bg-pink-50"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      PDF
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {donations.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Heart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No hay donaciones</p>
                      </div>
                    ) : (
                      donations.map((don) => (
                        <button
                          key={don.id}
                          data-testid={`donation-card-${don.id}`}
                          onClick={() => setSelectedDonation(don)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            selectedDonation?.id === don.id ? 'border-pink-400 bg-pink-50' : 'border-gray-200 hover:border-pink-300'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <span className="font-semibold text-gray-900 text-sm truncate">{don.donor_name}</span>
                            <span className="font-bold text-pink-600 text-sm">{formatCurrency(don.amount)}</span>
                          </div>
                          {don.company_name && <p className="text-xs text-gray-600 truncate mb-1">{don.company_name}</p>}
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${DONATION_STATUS[don.status]?.color}`}>
                              {DONATION_STATUS[don.status]?.label}
                            </span>
                            <span className="text-xs text-gray-400">{formatDate(don.created_at).split(',')[0]}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </Card>
              </div>
              <div className="lg:col-span-2">
                {selectedDonation ? (
                  <Card data-testid="donation-detail" className="p-6">
                    <div className="flex items-start justify-between mb-6 pb-6 border-b border-gray-200 gap-3 flex-wrap">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Monto donado</p>
                        <h2 className="text-3xl font-bold text-pink-600">{formatCurrency(selectedDonation.amount)}</h2>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          data-testid="download-receipt-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadReceipt(selectedDonation.id, selectedDonation.donor_name)}
                          className="border-sky-300 text-sky-700 hover:bg-sky-50"
                        >
                          <Download className="w-4 h-4 mr-1" /> Certificado
                        </Button>
                        <Button
                          data-testid="share-email-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => handleShareByEmail(selectedDonation)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        >
                          <Mail className="w-4 h-4 mr-1" /> Email
                        </Button>
                        <Button
                          data-testid="share-whatsapp-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => handleShareByWhatsApp(selectedDonation)}
                          className="border-green-300 text-green-700 hover:bg-green-50"
                        >
                          <Phone className="w-4 h-4 mr-1" /> WhatsApp
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid="delete-donation-btn" variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar donación?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction data-testid="confirm-delete-don-btn" onClick={() => handleDeleteDon(selectedDonation.id)} className="bg-red-600 hover:bg-red-700">
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <InfoRow icon={<User className="w-5 h-5" />} label="Donante" value={selectedDonation.donor_name} />
                      {selectedDonation.company_name && (
                        <InfoRow icon={<Building2 className="w-5 h-5" />} label="Empresa" value={selectedDonation.company_name} />
                      )}
                      <InfoRow icon={<Mail className="w-5 h-5" />} label="Email" value={selectedDonation.donor_email} href={`mailto:${selectedDonation.donor_email}`} />
                      <InfoRow icon={<Phone className="w-5 h-5" />} label="Teléfono" value={selectedDonation.donor_phone} href={`tel:${selectedDonation.donor_phone}`} />
                      <InfoRow icon={<FileText className="w-5 h-5" />} label="Método" value={selectedDonation.payment_method === 'transfer' ? 'Transferencia' : selectedDonation.payment_method} />
                      <InfoRow icon={<Calendar className="w-5 h-5" />} label="Registrada" value={formatDate(selectedDonation.created_at)} />
                    </div>
                    {selectedDonation.notes && (
                      <div className="mb-6">
                        <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Notas del donante</p>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <p className="text-gray-800 whitespace-pre-wrap">{selectedDonation.notes}</p>
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Estado</p>
                      <Select value={selectedDonation.status} onValueChange={(v) => handleDonStatusChange(selectedDonation.id, v)}>
                        <SelectTrigger data-testid="don-status-select" className="w-full sm:w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendiente</SelectItem>
                          <SelectItem value="confirmed">Confirmada</SelectItem>
                          <SelectItem value="verified">Verificada</SelectItem>
                          <SelectItem value="rejected">Rechazada</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-2">
                        Cambiá a "Verificada" cuando confirmes que la transferencia llegó a la cuenta.
                      </p>
                    </div>
                  </Card>
                ) : (
                  <EmptyDetail icon={<Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />} message="Selecciona una donación" />
                )}
              </div>
            </div>
          </TabsContent>

          {/* === GALLERY === */}
          <TabsContent value="gallery">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card className="p-5">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <Upload className="w-4 h-4 mr-2" /> Subir contenido
                  </h3>
                  <form onSubmit={handleUploadGallery} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Archivo (foto o video, máx 5MB)</label>
                      <input
                        ref={fileInputRef}
                        data-testid="gallery-file-input"
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
                      />
                    </div>
                    {uploadPreview && (
                      <div className="rounded-lg overflow-hidden border-2 border-sky-200">
                        {uploadForm.type === 'video' ? (
                          <video src={uploadPreview} className="w-full h-32 object-cover" controls />
                        ) : (
                          <img src={uploadPreview} alt="preview" className="w-full h-32 object-cover" />
                        )}
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Título *</label>
                      <Input
                        data-testid="gallery-title-input"
                        value={uploadForm.title}
                        onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                        placeholder="Ej: Entrenamiento en cancha"
                        className="border-2 border-gray-200 focus:border-sky-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descripción</label>
                      <Textarea
                        data-testid="gallery-description-input"
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                        rows={2}
                        placeholder="Opcional"
                        className="border-2 border-gray-200 focus:border-sky-400 resize-none"
                      />
                    </div>
                    <Button
                      data-testid="gallery-upload-btn"
                      type="submit"
                      disabled={uploading || !uploadPreview}
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Subiendo...' : 'Subir a galería'}
                    </Button>
                  </form>
                </Card>
              </div>
              <div className="lg:col-span-2">
                <Card className="p-5">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2" /> Contenido subido ({galleryItems.length})
                  </h3>
                  {galleryItems.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-30" />
                      <p>Aún no subiste contenido propio</p>
                      <p className="text-xs mt-1">La galería pública muestra imágenes por defecto hasta que subas las tuyas</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {galleryItems.map((item) => (
                        <div
                          key={item.id}
                          data-testid={`gallery-admin-item-${item.id}`}
                          className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group"
                        >
                          {item.type === 'video' ? (
                            <video src={item.data_url} className="w-full h-full object-cover" />
                          ) : (
                            <img src={item.data_url} alt={item.title} className="w-full h-full object-cover" />
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-3">
                            <p className="text-white font-semibold text-sm text-center mb-2 line-clamp-2">{item.title}</p>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button data-testid={`delete-gallery-${item.id}`} size="sm" variant="destructive">
                                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Eliminar este contenido?</AlertDialogTitle>
                                  <AlertDialogDescription>Se quitará permanentemente de la galería pública.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteGallery(item.id)} className="bg-red-600 hover:bg-red-700">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* === STATS === */}
          <TabsContent value="stats">
            <Card className="p-6 max-w-3xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Estadísticas Públicas</h2>
                  <p className="text-gray-600 text-sm">Estos números se muestran en el sitio web público</p>
                </div>
                {!editingStats ? (
                  <Button data-testid="edit-stats-btn" onClick={() => setEditingStats(true)} className="bg-sky-600 hover:bg-sky-700 text-white">
                    <Edit className="w-4 h-4 mr-2" /> Editar
                  </Button>
                ) : (
                  <Button data-testid="cancel-stats-btn" variant="outline" onClick={() => { setEditingStats(false); setStatsForm(adminStats.public_stats); }}>
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                )}
              </div>
              <form onSubmit={handleStatsUpdate} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <StatInput testid="stat-athletes-input" icon={<Users className="w-4 h-4 mr-2 text-sky-600" />} label="Atletas Beneficiados" value={statsForm.athletes_helped} onChange={(v) => setStatsForm({ ...statsForm, athletes_helped: v })} disabled={!editingStats} />
                  <StatInput testid="stat-provinces-input" icon={<MapPin className="w-4 h-4 mr-2 text-sky-600" />} label="Provincias Alcanzadas" value={statsForm.provinces_reached} onChange={(v) => setStatsForm({ ...statsForm, provinces_reached: v })} disabled={!editingStats} max="24" />
                  <StatInput testid="stat-success-input" icon={<TrendingUp className="w-4 h-4 mr-2 text-sky-600" />} label="Tasa de Éxito (%)" value={statsForm.success_rate} onChange={(v) => setStatsForm({ ...statsForm, success_rate: v })} disabled={!editingStats} max="100" />
                  <StatInput testid="stat-years-input" icon={<Award className="w-4 h-4 mr-2 text-sky-600" />} label="Años de Trayectoria" value={statsForm.years_active} onChange={(v) => setStatsForm({ ...statsForm, years_active: v })} disabled={!editingStats} />
                </div>
                {editingStats && (
                  <Button data-testid="save-stats-btn" type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
                    <Save className="w-4 h-4 mr-2" /> Guardar Cambios
                  </Button>
                )}
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const InfoRow = ({ icon, label, value, href, colSpan }) => (
  <div className={`flex items-start space-x-3 ${colSpan ? 'sm:col-span-2' : ''}`}>
    <div className="text-sky-600 mt-0.5">{icon}</div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-500 font-semibold uppercase">{label}</p>
      {href ? (
        <a href={href} className="text-sky-600 hover:underline break-all">{value}</a>
      ) : (
        <p className="text-gray-900 break-all">{value}</p>
      )}
    </div>
  </div>
);

const EmptyDetail = ({ message, icon }) => (
  <Card className="p-12 text-center">
    {icon || <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />}
    <h3 className="text-xl font-semibold text-gray-700 mb-2">{message}</h3>
    <p className="text-gray-500">Elige un elemento de la lista para ver los detalles</p>
  </Card>
);

const StatInput = ({ testid, icon, label, value, onChange, disabled, max }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
      {icon}{label}
    </label>
    <Input
      data-testid={testid}
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      min="0"
      max={max}
      className="border-2 border-gray-200 focus:border-sky-400 disabled:bg-gray-50"
    />
  </div>
);

export default Dashboard;
